import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { UserStatusProvider, useUserStatus } from './contexts/UserStatusContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import ModuleErrorBoundary from './components/common/ModuleErrorBoundary';
import TestModeBanner from './components/common/TestModeBanner';
import './App.css';

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Lazy load all components - only load when route is accessed
// Public routes (can be loaded without authentication)
const LoginPage = lazy(() => import('./components/common/LoginPage'));

// Protected routes (require authentication)
const EmailVerificationPage = lazy(() => import('./components/common/EmailVerificationPage'));
const CheckingAccessPage = lazy(() => import('./components/common/CheckingAccessPage'));
const Dashboard = lazy(() => import('./components/common/Dashboard'));
const ProfilePage = lazy(() => import('./components/common/ProfilePage'));
const SalesDashBoard = lazy(() => import('./components/my-tasks/sales/SalesDashBoard'));
const SalesTaskList = lazy(() => import('./components/my-tasks/sales/SalesTaskList'));
const PREDashBoard = lazy(() => import('./components/my-tasks/partner-relation-executive/PREDashBoard'));
const PRETaskList = lazy(() => import('./components/my-tasks/partner-relation-executive/PRETaskList'));
const AccountsDashBoard = lazy(() => import('./components/my-tasks/accounts/AccountsDashBoard'));
const AccountsTaskList = lazy(() => import('./components/my-tasks/accounts/AccountsTaskList'));
const BackEndDashBoard = lazy(() => import('./components/my-tasks/back-end/BackEndDashBoard'));
const BackEndTaskList = lazy(() => import('./components/my-tasks/back-end/BackEndTaskList'));
const TaskManagerDashBoard = lazy(() => import('./components/my-tasks/task-manager/TaskManagerDashBoard'));
const TaskManagerTaskList = lazy(() => import('./components/my-tasks/task-manager/TaskManagerTaskList'));
const RTODashBoard = lazy(() => import('./components/my-tasks/rto/RTODashBoard'));
const RTOTaskList = lazy(() => import('./components/my-tasks/rto/RTOTaskList'));
const ClosingStatus = lazy(() => import('./components/my-tasks/closing-status/ClosingStatus'));
const ApprovalPendings = lazy(() => import('./components/my-tasks/approval-pendings/ApprovalPendings'));
const TargetSettings = lazy(() => import('./components/my-tasks/target-settings/TargetSettings'));

// Company routes
const CompanyList = lazy(() => import('./components/company/CompanyList'));
const CompanyForm = lazy(() => import('./components/company/CompanyForm'));
const CompanyStageConfigPage = lazy(() => import('./components/company/CompanyStageConfigPage'));
const CompanyAdditionalConfig = lazy(() => import('./components/company/CompanyAdditionalConfig'));
const CompanyTasksList = lazy(() => import('./components/company/CompanyTasksList'));
const CompanyExecutiveDetails = lazy(() => import('./components/company/CompanyExecutiveDetails'));
const CompanyDocumentsConfig = lazy(() => import('./components/company/CompanyDocumentsConfig'));

// Agent routes
const AgentRegistrationForm = lazy(() => import('./components/agent/AgentRegistrationForm'));

// Dealer routes
const DealersList = lazy(() => import('./components/dealer/DealersList'));
const DealerForm = lazy(() => import('./components/dealer/DealerForm'));
const DealerEnquiries = lazy(() => import('./components/dealer/DealerEnquiries'));
const DealerRTO = lazy(() => import('./components/dealer/DealerRTO'));
const EMICalculator = lazy(() => import('./components/dealer/EMICalculator'));

// User management routes
const UserList = lazy(() => import('./components/user/UserList'));
const UserRegistrationForm = lazy(() => import('./components/user/UserRegistrationForm'));
const UserEditForm = lazy(() => import('./components/user/UserEditForm'));
const UserRoleList = lazy(() => import('./components/user/UserRoleList'));
const UserRoleForm = lazy(() => import('./components/user/UserRoleForm'));
const RoleHierarchy = lazy(() => import('./components/user/RoleHierarchy'));
const MergeUsers = lazy(() => import('./components/user/MergeUsers'));

// Settings routes
const UserPageList = lazy(() => import('./components/settings/UserPageList'));
const UserPageForm = lazy(() => import('./components/settings/UserPageForm'));
const ServiceList = lazy(() => import('./components/settings/ServiceList'));
const ServiceForm = lazy(() => import('./components/settings/ServiceForm'));
const BrandList = lazy(() => import('./components/settings/BrandList'));
const BrandForm = lazy(() => import('./components/settings/BrandForm'));
const HolidayCalendar = lazy(() => import('./components/settings/HolidayCalendar'));
const DocumentList = lazy(() => import('./components/settings/DocumentList'));
const DocumentForm = lazy(() => import('./components/settings/DocumentForm'));
const EnquiryList = lazy(() => import('./components/enquiry/EnquiryList'));
const EnquiryForm = lazy(() => import('./components/enquiry/EnquiryForm'));
const UsedBikeLoanForm = lazy(() => import('./components/enquiry/UsedBikeLoanForm'));
const EnquiryExternalEdit = lazy(() => import('./components/enquiry-external-edit/EnquiryExternalEdit'));
const ManageEnquiryList = lazy(() => import('./components/enquiry/ManageEnquiryList'));
const DelayReport = lazy(() => import('./components/common/DelayReport'));

function AppContent() {
  const { currentUser } = useAuth();
  const { isUserBlockedOrNotApproved } = useUserStatus();
  const [profileComplete, setProfileComplete] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [profileCheckKey, setProfileCheckKey] = useState(0);

  // Check profile completion
  useEffect(() => {
    const checkProfile = async () => {
      if (!currentUser || !currentUser.emailVerified) {
        setProfileComplete(null);
        setCheckingProfile(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfileComplete(!!userData.name);
        } else {
          setProfileComplete(false);
        }
      } catch (error) {
        console.error('Error checking profile:', error);
        setProfileComplete(false);
      } finally {
        setCheckingProfile(false);
      }
    };

    checkProfile();
  }, [currentUser, profileCheckKey]);

  // Expose refresh function via window for ProfilePage to call
  useEffect(() => {
    window.refreshProfileCheck = () => {
      setProfileCheckKey(prev => prev + 1);
    };
    return () => {
      delete window.refreshProfileCheck;
    };
  }, []);

  // Protected route component
  const ProtectedRoute = ({ children, requireProfile = false }) => {
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }
    if (!currentUser.emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }
    // Redirect blocked or unapproved users to profile
    if (isUserBlockedOrNotApproved()) {
      return <Navigate to="/profile" replace />;
    }
    if (requireProfile && profileComplete === false) {
      return <Navigate to="/profile" replace />;
    }
    return children;
  };

  // Profile route - accessible if verified but doesn't require profile completion
  const ProfileRoute = ({ children }) => {
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }
    if (!currentUser.emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }
    return children;
  };

  // Show loading while checking profile
  if (checkingProfile && currentUser && currentUser.emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ModuleErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path="/login"
            element={
              currentUser && !currentUser.emailVerified
                ? <Navigate to="/verify-email" replace />
                : <LoginPage />
            }
          />
          <Route
            path="/verify-email"
            element={
              currentUser
                ? <EmailVerificationPage />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/checking-access"
            element={
              currentUser && currentUser.emailVerified
                ? <CheckingAccessPage />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/profile"
            element={
              <ProfileRoute>
                <ProfilePage />
              </ProfileRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/closing-status"
            element={
              <ProtectedRoute requireProfile={true}>
                <ClosingStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/approval-pendings"
            element={
              <ProtectedRoute requireProfile={true}>
                <ApprovalPendings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/target-settings"
            element={
              <ProtectedRoute requireProfile={true}>
                <TargetSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <SalesDashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <SalesTaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealer-enquiries"
            element={
              <ProtectedRoute requireProfile={true}>
                <DealerEnquiries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealer-rto"
            element={
              <ProtectedRoute requireProfile={true}>
                <DealerRTO />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealer-emi-calculator"
            element={
              <ProtectedRoute requireProfile={true}>
                <EMICalculator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-relation-executive-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <PREDashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-relation-executive-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <PRETaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <AccountsDashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <AccountsTaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/back-end-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <BackEndDashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/back-end-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <BackEndTaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-manager-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <TaskManagerDashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-manager-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <TaskManagerTaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rto-dashboard"
            element={
              <ProtectedRoute requireProfile={true}>
                <RTODashBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rto-dashboard/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <RTOTaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id/stages"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyStageConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id/additional-config"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyAdditionalConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id/tasks"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyTasksList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id/executive"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyExecutiveDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/edit/:id/documents"
            element={
              <ProtectedRoute requireProfile={true}>
                <CompanyDocumentsConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent-registration"
            element={
              <ProtectedRoute requireProfile={true}>
                <AgentRegistrationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealers"
            element={
              <ProtectedRoute requireProfile={true}>
                <DealersList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealers/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <DealerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealers/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <DealerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserRegistrationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserEditForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/merge"
            element={
              <ProtectedRoute requireProfile={true}>
                <MergeUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-roles"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserRoleList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-roles/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserRoleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-roles/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserRoleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/role-hierarchy"
            element={
              <ProtectedRoute requireProfile={true}>
                <RoleHierarchy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-pages"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserPageList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-pages/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserPageForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-pages/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <UserPageForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute requireProfile={true}>
                <ServiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <ServiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <ServiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <ProtectedRoute requireProfile={true}>
                <BrandList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <BrandForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <BrandForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holiday-calendar"
            element={
              <ProtectedRoute requireProfile={true}>
                <HolidayCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute requireProfile={true}>
                <DocumentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <DocumentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <DocumentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiries/used-bike-loan/active"
            element={
              <ProtectedRoute requireProfile={true}>
                <EnquiryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delay-report"
            element={
              <ProtectedRoute requireProfile={true}>
                <DelayReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiries/used-bike-loan/inactive"
            element={
              <ProtectedRoute requireProfile={true}>
                <ManageEnquiryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiry-edit/:token"
            element={<EnquiryExternalEdit />}
          />
          <Route
            path="/enquiries/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <EnquiryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiries/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <EnquiryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/used-bike-loans/add"
            element={
              <ProtectedRoute requireProfile={true}>
                <UsedBikeLoanForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/used-bike-loans/edit/:id"
            element={
              <ProtectedRoute requireProfile={true}>
                <UsedBikeLoanForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              currentUser && currentUser.emailVerified
                ? (isUserBlockedOrNotApproved() || profileComplete === false
                  ? <Navigate to="/profile" replace />
                  : <Navigate to="/profile" replace />)
                : currentUser && !currentUser.emailVerified
                  ? <Navigate to="/verify-email" replace />
                  : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </Suspense>
    </ModuleErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <TestModeBanner />
      <AuthProvider>
        <UserStatusProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </UserStatusProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
